import type { StampPosition } from './models';

export interface PageRect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface StampSize {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

const MIN_MARGIN = 18;
const MAX_MARGIN = 36;
const MARGIN_RATIO = 0.05;

function clamp(value: number, min: number, max: number): number {
  if (min > max) {
    return min;
  }

  if (value < min) {
    return min;
  }

  if (value > max) {
    return max;
  }

  return value;
}

export function deriveUniformMargin(pageRect: PageRect): number {
  const width = pageRect.maxX - pageRect.minX;
  const height = pageRect.maxY - pageRect.minY;
  const shortestEdge = Math.min(width, height);
  const derivedMargin = shortestEdge * MARGIN_RATIO;

  return clamp(derivedMargin, MIN_MARGIN, MAX_MARGIN);
}

export function mapStampOrigin(pageRect: PageRect, position: StampPosition, stampSize: StampSize): Point {
  const margin = deriveUniformMargin(pageRect);

  const minX = pageRect.minX + margin;
  const maxX = pageRect.maxX - margin - stampSize.width;
  const minY = pageRect.minY + margin;
  const maxY = pageRect.maxY - margin - stampSize.height;

  const centerX = pageRect.minX + (pageRect.maxX - pageRect.minX) / 2 - stampSize.width / 2;

  const rawX =
    position === 'topLeft' || position === 'bottomLeft'
      ? minX
      : position === 'topCenter' || position === 'bottomCenter'
        ? centerX
        : maxX;

  const rawY = position.startsWith('top') ? maxY : minY;

  return {
    x: clamp(rawX, minX, maxX),
    y: clamp(rawY, minY, maxY),
  };
}
